import React, { useState, useEffect, useCallback } from "react";
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Pagination, Alert, CircularProgress, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import { Refresh as RefreshIcon, Visibility as VisibilityIcon, CheckCircle as CheckCircleIcon, Pending as PendingIcon, Lock as LockIcon } from "@mui/icons-material";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

function TransactionDetailsPanel() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        month: "",
        year: "",
        status: "",
        search: "",
    });
    const [batchMonthPairs, setBatchMonthPairs] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    });
    const [summary, setSummary] = useState({
        totalTransactions: 0,
        paidTransactions: 0,
        pendingTransactions: 0,
        totalAmount: 0,
    });
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

    useEffect(() => {
        const loadBatchMonths = async () => {
            try {
                const monthRes = await getData(API_ENDPOINTS.getPayrollMonthsDropdown);
                const months = monthRes?.result?.months || [];

                const parsed = months
                    .map((monthKey) => {
                        const [year, month] = String(monthKey).split("-");
                        const monthNumber = Number(month);
                        const yearNumber = Number(year);
                        if (!yearNumber || !monthNumber) return null;
                        const date = new Date(yearNumber, monthNumber - 1, 1);
                        return {
                            key: `${year}-${String(monthNumber).padStart(2, "0")}`,
                            month: date.toLocaleString("default", { month: "long" }),
                            year: String(yearNumber),
                        };
                    })
                    .filter(Boolean);

                setBatchMonthPairs(parsed);

                if (parsed.length > 0) {
                    setFilters((prev) => ({
                        ...prev,
                        month: parsed[0].month,
                        year: parsed[0].year,
                    }));
                }
            } catch (err) {
                return null;
            }
        };

        loadBatchMonths();
    }, []);

    const fetchTransactionDetails = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                ...filters,
                page: pagination.page,
                limit: pagination.limit,
            };

            // Remove empty filters
            Object.keys(params).forEach((key) => {
                if (!params[key]) delete params[key];
            });

            // Using the query string builder logic from your endpoints file
            // Note: pass the params object or stringify it based on your helper logic
            const queryString = new URLSearchParams(params).toString();
            const response = await getData(API_ENDPOINTS.getTransactionDetails(queryString));

            if (response.statusCode === 200) {
                setTransactions(response.result.transactions);
                setPagination(response.result.pagination);
                setSummary(response.result.summary);
            } else {
                setError(response.message || "Failed to fetch transaction details");
            }
        } catch (err) {
            setError("Failed to fetch transaction details");
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.page, pagination.limit]);

    useEffect(() => {
        fetchTransactionDetails();
    }, [fetchTransactionDetails]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
        setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
    };

    const handlePageChange = (event, value) => {
        setPagination((prev) => ({ ...prev, page: value }));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Paid":
                return "success";
            case "Locked":
                return "warning";
            case "Pending":
                return "default";
            default:
                return "default";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "Paid":
                return <CheckCircleIcon fontSize="small" />;
            case "Locked":
                return <LockIcon fontSize="small" />;
            case "Pending":
                return <PendingIcon fontSize="small" />;
            default:
                return null;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleViewDetails = (transaction) => {
        setSelectedTransaction(transaction);
        setDetailsDialogOpen(true);
    };

    const months = Array.from(new Set(batchMonthPairs.map((item) => item.month)));
    const years = Array.from(new Set(batchMonthPairs.map((item) => item.year))).sort(
        (a, b) => Number(b) - Number(a),
    );
    const normalizedSearch = filters.search.trim().toLowerCase();
    const filteredTransactions = normalizedSearch
        ? transactions.filter((transaction) =>
            [
                transaction.employeeName,
                transaction.empId,
                transaction.status,
                transaction.month,
                transaction.year,
                transaction.razorpay_payment_id,
                transaction.razorpay_order_id,
            ].some((value) => String(value ?? "").toLowerCase().includes(normalizedSearch)),
        )
        : transactions;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                Transaction Details
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ bgcolor: "#e3f2fd" }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Transactions
                            </Typography>
                            <Typography variant="h4" component="div">
                                {summary.totalTransactions}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ bgcolor: "#e8f5e8" }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Paid Transactions
                            </Typography>
                            <Typography variant="h4" component="div" color="success.main">
                                {summary.paidTransactions}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ bgcolor: "#fff3e0" }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Pending Transactions
                            </Typography>
                            <Typography variant="h4" component="div" color="warning.main">
                                {summary.pendingTransactions}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ bgcolor: "#f3e5f5" }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Amount
                            </Typography>
                            <Typography variant="h4" component="div" color="primary.main">
                                {formatCurrency(summary.totalAmount)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Month</InputLabel>
                            <Select
                                value={filters.month}
                                label="Month"
                                onChange={(e) => handleFilterChange("month", e.target.value)}
                            >
                                <MenuItem value="">All Months</MenuItem>
                                {months.map((month) => (
                                    <MenuItem key={month} value={month}>
                                        {month}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={filters.year}
                                label="Year"
                                onChange={(e) => handleFilterChange("year", e.target.value)}
                            >
                                <MenuItem value="">All Years</MenuItem>
                                {years.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) => handleFilterChange("status", e.target.value)}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="locked">Locked</MenuItem>
                                <MenuItem value="finalized">Finalized</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            label="Search"
                            value={filters.search}
                            onChange={(e) => handleFilterChange("search", e.target.value)}
                            placeholder="Employee, emp id, month"
                            sx={{ minWidth: 240 }}
                        />

                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Page Size</InputLabel>
                            <Select
                                value={pagination.limit}
                                label="Page Size"
                                onChange={(e) => {
                                    const newLimit = parseInt(e.target.value);
                                    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
                                }}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchTransactionDetails}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Transactions Table */}
            <Card>
                <CardContent>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "grey.50" }}>
                                            <TableCell>
                                                <strong>Employee</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Period</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>Net Salary</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>Reimbursements</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>Leave Encashment</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>Total Amount</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Status</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>Transaction Date</strong>
                                            </TableCell>
                                            <TableCell align="center">
                                                <strong>Actions</strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredTransactions.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                                    <Typography color="textSecondary">
                                                        No transactions found for the selected filters
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredTransactions.map((transaction) => (
                                                <TableRow key={transaction.id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {transaction.employeeName}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                            >
                                                                {transaction.empId}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {transaction.month} {transaction.year}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(transaction.netSalary)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(transaction.reimbursementAmount)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(transaction.leaveEncashmentAmount)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography fontWeight="medium">
                                                            {formatCurrency(transaction.totalAmount)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={getStatusIcon(transaction.status)}
                                                            label={transaction.status}
                                                            color={getStatusColor(transaction.status)}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDate(transaction.transactionDate)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewDetails(transaction)}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 2,
                                        mt: 3,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Page {pagination.page} of {pagination.totalPages} • {pagination.total} total transactions
                                    </Typography>
                                    <Pagination
                                        count={pagination.totalPages}
                                        page={pagination.page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Transaction Details Dialog */}
            <AppDialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Transaction Details
                    {selectedTransaction && (
                        <Typography variant="subtitle2" color="textSecondary">
                            {selectedTransaction.employeeName} - {selectedTransaction.month}{" "}
                            {selectedTransaction.year}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {selectedTransaction && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Employee ID
                                </Typography>
                                <Typography>{selectedTransaction.empId}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Email
                                </Typography>
                                <Typography>{selectedTransaction.email || "N/A"}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Pay Period
                                </Typography>
                                <Typography>
                                    {selectedTransaction.month} {selectedTransaction.year}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Status
                                </Typography>
                                <Chip
                                    icon={getStatusIcon(selectedTransaction.status)}
                                    label={selectedTransaction.status}
                                    color={getStatusColor(selectedTransaction.status)}
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="h6" gutterBottom>
                                    Attendance Details
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Total Days
                                </Typography>
                                <Typography>{selectedTransaction.numOfDays || 0}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Working Days
                                </Typography>
                                <Typography>{selectedTransaction.workingDays || 0}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    LOP Count
                                </Typography>
                                <Typography>{selectedTransaction.lopCount || 0}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    LOP Amount
                                </Typography>
                                <Typography>{formatCurrency(selectedTransaction.lopAmount || 0)}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="h6" gutterBottom>
                                    Payment Breakdown
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Net Salary
                                </Typography>
                                <Typography variant="h6">
                                    {formatCurrency(selectedTransaction.netSalary)}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Reimbursements
                                </Typography>
                                <Typography variant="h6">
                                    {formatCurrency(selectedTransaction.reimbursementAmount)}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Leave Encashment
                                </Typography>
                                <Typography variant="h6">
                                    {formatCurrency(selectedTransaction.leaveEncashmentAmount)}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box
                                    sx={{ borderTop: 1, borderColor: "divider", pt: 2, mt: 2 }}
                                >
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Total Amount
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        color="primary.main"
                                        fontWeight="bold"
                                    >
                                        {formatCurrency(selectedTransaction.totalAmount)}
                                    </Typography>
                                </Box>
                            </Grid>
                            {selectedTransaction.razorpay_order_id && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Razorpay Order ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                        {selectedTransaction.razorpay_order_id}
                                    </Typography>
                                </Grid>
                            )}
                            {selectedTransaction.razorpay_payment_id && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Razorpay Payment ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                        {selectedTransaction.razorpay_payment_id}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Transaction Date
                                </Typography>
                                <Typography>
                                    {formatDate(selectedTransaction.transactionDate)}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </AppDialog>
        </Box>
    );
}

export default TransactionDetailsPanel;